import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Tabs,
  Tab,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import api from '@/services/authService';

interface SettingItem {
  key: string;
  label: string;
  type: 'text' | 'number' | 'boolean' | 'select';
  options?: string[];
  value?: any;
}

interface CategorySettings {
  [key: string]: SettingItem;
}

const SETTINGS_CATEGORIES: Record<string, CategorySettings> = {
  general: {
    siteName: { key: 'siteName', label: 'Site Name', type: 'text' },
    siteDescription: { key: 'siteDescription', label: 'Site Description', type: 'text' },
    contactEmail: { key: 'contactEmail', label: 'Contact Email', type: 'text' },
    maintenanceMode: { key: 'maintenanceMode', label: 'Maintenance Mode', type: 'boolean' },
    registrationEnabled: { key: 'registrationEnabled', label: 'Registration Enabled', type: 'boolean' },
    defaultCurrency: { key: 'defaultCurrency', label: 'Default Currency', type: 'select', options: ['EUR', 'USD'] },
  },
  security: {
    passwordMinLength: { key: 'passwordMinLength', label: 'Minimum Password Length', type: 'number' },
    sessionTimeout: { key: 'sessionTimeout', label: 'Session Timeout (minutes)', type: 'number' },
    maxLoginAttempts: { key: 'maxLoginAttempts', label: 'Max Login Attempts', type: 'number' },
    twoFactorEnabled: { key: 'twoFactorEnabled', label: 'Two-Factor Authentication', type: 'boolean' },
  },
  notifications: {
    emailNotifications: { key: 'emailNotifications', label: 'Email Notifications', type: 'boolean' },
    smsNotifications: { key: 'smsNotifications', label: 'SMS Notifications', type: 'boolean' },
    pushNotifications: { key: 'pushNotifications', label: 'Push Notifications', type: 'boolean' },
  },
  email: {
    smtpEnabled: { key: 'smtpEnabled', label: 'Use Custom SMTP', type: 'boolean' },
    smtpHost: { key: 'smtpHost', label: 'SMTP Host', type: 'text' },
    smtpPort: { key: 'smtpPort', label: 'SMTP Port', type: 'number' },
    smtpSecure: { key: 'smtpSecure', label: 'Use TLS/SSL (secure)', type: 'boolean' },
    smtpUser: { key: 'smtpUser', label: 'SMTP Username', type: 'text' },
    smtpPass: { key: 'smtpPass', label: 'SMTP Password', type: 'text' },
    smtpFromAddress: { key: 'smtpFromAddress', label: 'From Email Address', type: 'text' },
    smtpFromName: { key: 'smtpFromName', label: 'From Name', type: 'text' },
  },
  financial: {
    dailyTransactionLimit: { key: 'dailyTransactionLimit', label: 'Daily Transaction Limit', type: 'number' },
    weeklyTransactionLimit: { key: 'weeklyTransactionLimit', label: 'Weekly Transaction Limit', type: 'number' },
    monthlyTransactionLimit: { key: 'monthlyTransactionLimit', label: 'Monthly Transaction Limit', type: 'number' },
    autoApprovalThreshold: { key: 'autoApprovalThreshold', label: 'Auto-Approval Threshold', type: 'number' },
    supportedCurrencies: { key: 'supportedCurrencies', label: 'Supported Currencies', type: 'select', options: ['EUR', 'USD', 'GBP'] },
    exchangeRateUpdateFrequency: { key: 'exchangeRateUpdateFrequency', label: 'Exchange Rate Update Frequency', type: 'select', options: ['hourly', 'daily', 'weekly'] },
  },
};

function TabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminSettings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Helper function to parse string values to correct types
  const parseSettingValue = (value: string, type: 'text' | 'number' | 'boolean' | 'select'): any => {
    if (type === 'boolean') {
      return value === 'true';
    }
    if (type === 'number') {
      return value ? parseFloat(value) : 0;
    }
    return value; // text and select remain as strings
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      const response = await api.get('/admin/settings', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        // Parse string values to correct types based on setting definition
        const parsedSettings: Record<string, any> = {};
        for (const [category, items] of Object.entries(response.data.data)) {
          parsedSettings[category] = {};
          if (items && typeof items === 'object') {
            for (const [key, value] of Object.entries(items)) {
              const settingDef = SETTINGS_CATEGORIES[category]?.[key];
              if (settingDef && typeof value === 'string') {
                parsedSettings[category][key] = parseSettingValue(value, settingDef.type);
              } else {
                parsedSettings[category][key] = value;
              }
            }
          }
        }
        setSettings(parsedSettings);
      } else {
        setError('Failed to fetch settings');
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('accessToken');
      
      // Convert settings object to array format for API
      const settingsArray = Object.entries(settings).flatMap(([category, items]) => {
        if (typeof items === 'object') {
          return Object.entries(items).map(([key, value]) => ({
            category,
            key,
            value: String(value), // Ensure value is always a string
          }));
        }
        return [];
      });

      console.log('Saving settings:', settingsArray);

      const response = await api.put('/admin/settings', settingsArray, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      console.log('Save response:', response.data);
      
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh settings to confirm they were saved
      fetchSettings();
    } catch (err: any) {
      console.error('Error saving settings:', err);
      const errorMessage = err.response?.data?.error?.message || err.message || 'Failed to save settings';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (category: string, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value,
      },
    }));
  };

  const renderSetting = (setting: SettingItem, category: string) => {
    const handleChange = (newValue: any) => {
      updateSetting(category, setting.key, newValue);
    };

    return (
      <Grid item xs={12} sm={6} md={4} key={setting.key}>
        <FormControl fullWidth>
          {setting.type === 'text' && (
            <TextField
              fullWidth
              label={setting.label}
              type={setting.key === 'smtpPass' ? 'password' : 'text'}
              value={settings[category]?.[setting.key] || ''}
              onChange={(e) => handleChange(e.target.value)}
              disabled={saving}
            />
          )}
          {setting.type === 'number' && (
            <TextField
              fullWidth
              type="number"
              label={setting.label}
              value={settings[category]?.[setting.key] || ''}
              onChange={(e) => handleChange(e.target.value)}
              disabled={saving}
              inputProps={{ min: 0 }}
            />
          )}
          {setting.type === 'boolean' && (
            <FormControlLabel
              control={
                <Switch
                  checked={settings[category]?.[setting.key] === true}
                  onChange={(e) => handleChange(e.target.checked)}
                  disabled={saving}
                />
              }
              label={setting.label}
            />
          )}
          {setting.type === 'select' && (
            <FormControl fullWidth>
              <InputLabel>{setting.label}</InputLabel>
              <Select
                value={settings[category]?.[setting.key] || ''}
                onChange={(e) => handleChange(e.target.value)}
                disabled={saving}
              >
                {setting.options?.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </FormControl>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">System Settings</Typography>
        <Button
          variant="contained"
          onClick={fetchSettings}
          startIcon={<RefreshIcon />}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Save Button */}
      <Box display="flex" justifyContent="flex-end" mb={3}>
        <Button
          variant="contained"
          onClick={saveSettings}
          startIcon={<SaveIcon />}
          disabled={saving}
          sx={{ ml: 2 }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="General" />
          <Tab label="Security" />
          <Tab label="Notifications" />
          <Tab label="Financial" />
          <Tab label="Email" />
        </Tabs>
      </Box>

      {/* General Settings */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>General Settings</Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>
              {Object.values(SETTINGS_CATEGORIES.general).map(setting => renderSetting(setting, 'general'))}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Security Settings */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Box display="flex" alignItems="center" gap={1}>
                <SecurityIcon sx={{ mr: 1 }} />
                Security Settings
              </Box>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>
              {Object.values(SETTINGS_CATEGORIES.security).map(setting => renderSetting(setting, 'security'))}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Notifications Settings */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Box display="flex" alignItems="center" gap={1}>
                <NotificationsIcon sx={{ mr: 1 }} />
                Notification Settings
              </Box>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>
              {Object.values(SETTINGS_CATEGORIES.notifications).map(setting => renderSetting(setting, 'notifications'))}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Financial Settings */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Box display="flex" alignItems="center" gap={1}>
                <AccountBalanceIcon sx={{ mr: 1 }} />
                Financial Settings
              </Box>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>
              {Object.values(SETTINGS_CATEGORIES.financial).map(setting => renderSetting(setting, 'financial'))}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Email / SMTP Settings */}
      <TabPanel value={tabValue} index={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Box display="flex" alignItems="center" gap={1}>
                <SettingsIcon sx={{ mr: 1 }} />
                Email / SMTP Settings
              </Box>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>
              {Object.values((SETTINGS_CATEGORIES as any).email).map((setting: SettingItem) => renderSetting(setting, 'email'))}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
};

export default AdminSettings;
